// PalettePanel — 16 色固定スロット (旧 Java applet 踏襲) のみを描画。
//
// Phase 6.7-B-fix Item 1 / Item 4: ARGB スライダ・現在色プレビューは別 component
// (`ArgbSliderPanel.tsx`) に分離。このファイルは 16 色グリッドのみを担う。
// 並びは 旧版 `room.png` 観察より **2 列 × 8 行 column-first** (idx 0..7 が左列,
// idx 8..15 が右列)。これは Java 版 `PaintToolCanvas.drawPaintColors()` がカラム単位
// で y を進めて描画していたのを CSS Grid `grid-flow-col` で再現。
//
// 出典: `OekakiChatJava/OekakiChatJavaApplet/src/oekakichat/applet/PaintToolCanvas.java:66-82`
// (LEGACY_PALETTE 配列値) / `room.png` (column-first 並び).
//
// (architecture.md §16 — クラシックレイアウト規定 / §19.3)
//
// Phase 6.7-B-fix-2A: 選択中マーカーを CSS pseudo (selectedMarker 文字列) から SVG
// `<SelectedMarker />` component に変更. 黒枠線が出ず黄色塊に見えていた問題を解消
// (旧版 PaintToolCanvas.java:645-672 と同じ orange fill + black stroke を SVG で
// 一発再現)。
//
// 2026-05-17 slot 永続化 (ユーザ fb): 旧設計では「選択中 cell の bg を pen.color に
// 追従、非選択 cell は LEGACY_PALETTE[idx] の固定色」だったため、カラーホイール /
// ARGB スライダ / スポイトで色を変えた後に別 slot をクリックすると、それまでの
// カスタム色が失われていた (= 「カラーホイールで色を変えてもパレットの選択を移すと
// 色がリセットされる」). 対策として slot 永続化 を caller (ClassicLayout) に移管:
// - 16 色配列を caller の state (`paletteSlots`) で保持し、props で受け取る.
// - 選択中 idx も caller (`selectedPaletteIdx`) で保持し、props で受け取る.
// - 色変更経路 (ArgbSliderPanel / ColorWheelPalette / spuit) は caller の
//   `onColorChange` で paletteSlots[selectedPaletteIdx] を上書きする.
// これで「カスタム色を作って別 slot に切替 → 戻る → カスタム色が復元」が成立する.
// 元コメントで Phase 10「カスタムパレット」スコープとしていた話を α 期に前倒し.
//
// 申し送り (Phase 6.7-C R7 で再利用):
// - `outsetFrame` / `insetFrame` / `<SelectedMarker />` は `legacyFrames.tsx` に
//   集約済。将来 RoomSettingsModal や入室画面のフォーム枠も同 token で揃える際に
//   流用可能。

import { useState } from 'react';

import type { PenConfig } from '../../../domain/PenConfig.js';
import { ConfirmDialog } from '../../ConfirmDialog.js';
import { useTapActivate } from '../../hooks/useTapActivate.js';
import { outsetFrame, SelectedMarker } from './legacyFrames.js';

/**
 * 旧 Java applet の `drawColors` ハードコードを完全踏襲した 16 色パレット (= 初期値).
 * (`PaintToolCanvas.java:66-82`、配列順の確定値)
 *
 * 配列順 (column-first で 2 × 8 表示):
 *   左列 (idx 0..7):  black / gray / lightgray / white / 茶 / 肌 / 薄肌 / 薄桃
 *   右列 (idx 8..15): 赤 / ピンク / 紫 / 青 / シアン / 緑 / 深緑 / 黄
 *
 * UI 表示は `grid-cols-2 grid-rows-8 grid-flow-col` で配列順を 縦方向に流す。
 *
 * 2026-05-17 slot 永続化以降は「**初期値**としての固定 16 色」を意味する.
 * runtime の slot 色は caller (ClassicLayout) の `paletteSlots` state が持ち、
 * カラーホイール等の操作で個別 slot を上書き可能.
 */
export const LEGACY_PALETTE: ReadonlyArray<number> = [
  0x000000, 0x606060, 0xa0a0a0, 0xffffff, 0xbc700c, 0xec9d86, 0xffc39e, 0xffd9c2, 0xff0000,
  0xffacac, 0xd894e0, 0x0000ff, 0x00ffff, 0x50ff50, 0x50bc50, 0xffff00,
];

/**
 * 後方互換エイリアス. `MaskPanel` の MASK 色ピッカー等が import している.
 * Phase 6.7-B 以降の新規コードは `LEGACY_PALETTE` を直接参照すること.
 */
export const DEFAULT_PALETTE = LEGACY_PALETTE;

/** 6 桁 hex 文字列に整形 (aria-label / 表示・テスト互換のため zero-pad 必須). */
const toCss = (rgb: number): string => `#${(rgb & 0xffffff).toString(16).padStart(6, '0')}`;

/** 8bit 各チャネル → RGB24 合成 (0..0xFFFFFF にクランプ). */
export const rgbFromChannels = (r: number, g: number, b: number): number => {
  const cr = Math.max(0, Math.min(255, r | 0));
  const cg = Math.max(0, Math.min(255, g | 0));
  const cb = Math.max(0, Math.min(255, b | 0));
  return ((cr << 16) | (cg << 8) | cb) >>> 0;
};

export interface PalettePanelProps {
  readonly pen: PenConfig;
  /**
   * 16 slot の現在色配列 (caller 所有, 長さ 16). 個別 slot の上書きは caller 側で
   * `onColorChange` 経路から `paletteSlots[selectedPaletteIdx] = c` で行う (2026-05-17
   * slot 永続化).
   */
  readonly palette: ReadonlyArray<number>;
  /** 「最後にクリックされた cell の idx」(0..15). caller 所有. */
  readonly selectedIdx: number;
  /** slot click 時に呼ばれる. caller は selectedIdx を更新し pen.color を slot の色に倒す. */
  readonly onSelectIdx: (idx: number) => void;
  /**
   * Phase 6.7 サブ task (consult 2026-05-07 → 修正): 旧 `ColorChartDialog.java`
   * (旧名「カラーサークル」) 踏襲のカラーホイール floating palette を開く.
   * caller (= ClassicLayout) が
   * `ColorWheelPalette` の表示状態を所有する責務. 単一クリックは従来通り
   * (= その slot の色を描画色に設定) のままで、ダブルクリック時のみ呼ばれる。
   *
   * MVP スコープ: dblclick で開いた palette は wheel/slider 操作のたびに
   * **描画色を即更新** (= ArgbSliderPanel と同経路, operate-as-you-go).
   * 2026-05-17 slot 永続化以降は wheel/slider 操作のたびに `paletteSlots[idx]`
   * も同時に上書きされるため、「dblclick した slot をカラーホイールで編集して
   * 別 slot に切替 → 戻る → 編集色が復元」が成立する.
   */
  readonly onColorDoubleClick?: (initialColor: number) => void;
  /**
   * α feedback (2026-06-13、consult 2026-07-25): パレットを初期 16 色
   * (`LEGACY_PALETTE`) に戻す。runtime slot 永続化はセッション内のみ (localStorage
   * 不使用) なので、セッション中に戻す手段がこれまで無かった。本 component 側で
   * 誤クリック防止の `ConfirmDialog` を挟んでから呼ぶ (= caller は callback 1 本のみ渡す)。
   */
  readonly onReset: () => void;
}

export const PalettePanel = ({
  pen,
  palette,
  selectedIdx,
  onSelectIdx,
  onColorDoubleClick,
  onReset,
}: PalettePanelProps): React.ReactNode => {
  // α feedback (2026-06-13): リセット確認モーダルの開閉。誤クリックで 16 色が
  // 一瞬で吹き飛ぶのを避けるためのワンクッション (= 全消しボタンの ConfirmDialog と
  // 同じ既存イディオムを踏襲、`window.confirm` は iPad Safari の全画面解除問題があり
  // 使わない)。
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // 起動を pointer ベースへ (ペン / Windows Ink の click 不発対策、ToolPanel と同じ
  // useTapActivate)。onClick (キーボード用) は tap 側が温存する。
  const resetTap = useTapActivate(() => setResetConfirmOpen(true));
  return (
    // `mb-1`: リセットボタンを **パレット側のグループに見せる** ための下マージン
    // (2026-07-25 ユーザ指摘)。ClassicLayout の親 fieldset が `gap-1` (4px) で
    // PalettePanel と ToolPanel を並べるため、これが無いと「グリッド↔ボタン間 8px /
    // ボタン↔ペン間 4px」となり、近接の原理でボタンが**ペン側の持ち物に見えてしまう**
    // (実測 7.1px vs 3.6px)。上を 4px・下を 8px に反転させて所属を明確にする。
    // 上下で ±4px の付け替えなので**左ペインの総高は不変** (= 縦溢れ対策 §2.4 に影響しない)。
    <div className="mb-1 flex flex-col gap-1">
      <fieldset
        aria-label="16 色グリッド"
        data-manual="color.palette"
        // 旧版 room.png 観察: 2 列 × 8 行, column-first (idx 0..7 が左列縦並び)。
        // grid-flow-col + grid-rows-8 で配列順を縦に流す。
        // gap / padding は旧版 PaintToolCanvas.java:404-413 踏襲: cell 同士の
        // ギャップ (旧版 3px) + 周囲マージン (旧版 3px) を Tailwind の小さい単位で
        // 再現 (gap-0.5 = 2px, p-1 = 4px)。R16 後ユーザ指摘で復元。
        className="grid grid-flow-col grid-cols-2 grid-rows-8 gap-0.5 p-1"
      >
        {palette.map((c, idx) => {
          const selected = idx === selectedIdx;
          // 選択中 cell の背景色は pen.color (動的) に追従. これは ARGB スライダ /
          // カラーホイール操作の途中で「現在描画色 = pen.color」が paletteSlots[idx]
          // に同期 setState される前のフレームでも見た目を即追従させるため.
          // 非選択 cell は `palette[idx]` (= caller が永続化している slot 色).
          const bgHex = toCss(selected ? pen.color : c);
          // aria-label は **現在の slot 色** を出す (= screen reader / e2e 自動探索で
          // 「今の色」が分かる). e2e (`snapshotResume.spec.ts`) はテスト開始時の
          // 初期値 (LEGACY_PALETTE と一致) で swatch を locate するため、初期状態で
          // 後方互換は保たれる (slot 永続化後も初期色を変えない限り label 一致).
          const labelHex = toCss(c);
          return (
            <button
              // 2026-05-17 slot 永続化: key は LEGACY_PALETTE[idx] (= 不変な初期色).
              // palette[idx] (= 可変な現在色) を key にすると上書き時に React が
              // cell を unmount/remount してしまうため. idx そのものは Biome
              // `noArrayIndexKey` で警告されるので、LEGACY_PALETTE の 16 値は
              // 全て distinct であることを利用して stable な key として使う.
              key={LEGACY_PALETTE[idx]}
              type="button"
              aria-label={`色 ${labelHex}`}
              aria-pressed={selected}
              onClick={() => onSelectIdx(idx)}
              // Phase 6.7 サブ task (consult 2026-05-07 → 修正): dblclick →
              // カラーホイール floating palette を開く. 旧 `ColorChartDialog.java`
              // (旧名「カラーサークル」) と同 UX (但し UI は floating panel +
              // 即反映に変更). 表示状態は caller (ClassicLayout) が持つ。
              // onClick が先に発火して selectedIdx が新 idx に倒れているので、
              // ここで onSelectIdx(idx) を二重に呼ぶ必要はない (caller 側で
              // 同一 idx の再 select は no-op).
              onDoubleClick={() => {
                // pen.color (= 直前の onClick 経路で slot の永続色に倒っている)
                // を初期値として渡す. wheel/slider 操作のたびに paletteSlots[idx]
                // も追従して永続化される (= ArgbSliderPanel と同経路).
                onColorDoubleClick?.(pen.color);
              }}
              // 静的 1px 黒 border は Phase 6.7-B-fix-2B-b で復活 (色被り判別のため)。
              // focus ring は旧 Java applet 完全再現要求により抑制 (識別は
              // SelectedMarker で代替)。relative は SelectedMarker absolute 用。
              className="relative h-6 w-full border border-black focus:outline-none focus-visible:outline-none"
              style={{ background: bgHex }}
            >
              {selected ? <SelectedMarker /> : null}
            </button>
          );
        })}
      </fieldset>

      {/* α feedback (2026-06-13): パレットリセット導線。16 色グリッドの直下に置く。
          間隔は root の `gap-1` (4px) のみに任せ、**追加の `mt-1` は付けない**
          (2026-07-25 ユーザ指摘: 上 8px / 下 4px だとペン側に属して見えた。root の
          `mb-1` と合わせて上 4px / 下 8px にし、パレット側のグループに見せる)。
          誤爆防止は「小さく・地味な配色にして slot に見せない」+ ConfirmDialog の
          ワンクッションが担う (間隔ではなく確認ダイアログが本丸)。 */}
      <button
        type="button"
        {...resetTap}
        data-manual="color.palette"
        title="パレットを初期16色に戻す"
        className="h-4 w-full border border-black/50 bg-white text-[9px] font-bold text-black/70 hover:bg-[var(--color-accent-soft)] hover:text-black focus:outline-none focus-visible:outline-none"
      >
        初期色に戻す
      </button>

      <ConfirmDialog
        open={resetConfirmOpen}
        title="パレットをリセット"
        message={'パレットを初期16色に戻しますか?\nカスタムした色は失われます。'}
        confirmLabel="リセット"
        destructive
        onConfirm={() => {
          onReset();
          setResetConfirmOpen(false);
        }}
        onCancel={() => setResetConfirmOpen(false)}
      />
    </div>
  );
};

// outsetFrame は他 panel から re-export したい場面はないので, legacyFrames.ts から
// 直接 import する運用に統一 (ToolPanel / MaskPanel / ArgbSliderPanel も同様)。
export { outsetFrame };
